import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOrgId } from "@/hooks/useOrganization";
import { FEATURE_FLAGS } from "@/lib/feature-flags";

export type ModuleIdentityPreference = "off" | "subtle";
export type TableDensityPreference = "compact" | "comfortable";
export type DisplayDensityPreference = "compact" | "comfortable";

interface UiPreferences {
  moduleIdentity: ModuleIdentityPreference;
  tableDensity: TableDensityPreference;
  displayDensity: DisplayDensityPreference;
}

const defaultPreferences: UiPreferences = {
  moduleIdentity: FEATURE_FLAGS.ui.moduleIdentity,
  tableDensity: "comfortable",
  displayDensity: "comfortable",
};

function getStorageKey(userId: string | null | undefined, orgId: string | null | undefined) {
  if (!userId || !orgId) return null;
  return `ui-preferences:${orgId}:${userId}`;
}

function readPreferences(storageKey: string | null): UiPreferences | null {
  if (!storageKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as UiPreferences;
  } catch (error) {
    console.debug("[ui-preferences] unable to read preferences", error);
    return null;
  }
}

function writePreferences(storageKey: string | null, preferences: UiPreferences) {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
  } catch (error) {
    console.debug("[ui-preferences] unable to write preferences", error);
  }
}

export function useUiPreferences() {
  const { user } = useAuth();
  const organizationId = useOrgId();
  const storageKey = useMemo(
    () => getStorageKey(user?.id, organizationId),
    [user?.id, organizationId],
  );

  const [moduleIdentity, setModuleIdentity] = useState<ModuleIdentityPreference>(
    defaultPreferences.moduleIdentity,
  );
  const [tableDensity, setTableDensity] = useState<TableDensityPreference>(
    defaultPreferences.tableDensity,
  );
  const [displayDensity, setDisplayDensity] = useState<DisplayDensityPreference>(
    defaultPreferences.displayDensity,
  );
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const stored = readPreferences(storageKey);
    if (stored?.moduleIdentity) {
      setModuleIdentity(stored.moduleIdentity);
    } else {
      setModuleIdentity(defaultPreferences.moduleIdentity);
    }
    if (stored?.tableDensity) {
      setTableDensity(stored.tableDensity);
    } else {
      setTableDensity(defaultPreferences.tableDensity);
    }
    if (stored?.displayDensity) {
      setDisplayDensity(stored.displayDensity);
    } else {
      setDisplayDensity(defaultPreferences.displayDensity);
    }
    setIsReady(true);
  }, [storageKey]);

  // Sync density data attribute to <html> for CSS variable switching
  useEffect(() => {
    if (!isReady) return;
    document.documentElement.setAttribute("data-density", displayDensity);
  }, [displayDensity, isReady]);

  useEffect(() => {
    if (!isReady) return;
    writePreferences(storageKey, { moduleIdentity, tableDensity, displayDensity });
  }, [storageKey, moduleIdentity, tableDensity, displayDensity, isReady]);

  const updateModuleIdentity = useCallback((next: ModuleIdentityPreference) => {
    setModuleIdentity(next);
  }, []);
  const updateTableDensity = useCallback((next: TableDensityPreference) => {
    setTableDensity(next);
  }, []);
  const updateDisplayDensity = useCallback((next: DisplayDensityPreference) => {
    setDisplayDensity(next);
  }, []);

  return {
    moduleIdentity,
    setModuleIdentity: updateModuleIdentity,
    tableDensity,
    setTableDensity: updateTableDensity,
    displayDensity,
    setDisplayDensity: updateDisplayDensity,
    isReady,
  };
}
